import React from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { motion } from 'framer-motion';
import { Edit2, Trash2, Calendar, AlertCircle } from 'lucide-react';
import { Task } from '../types';

interface TaskCardProps {
  task: Task;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, index, onEdit, onDelete }) => {
  const getPriorityColor = (): string => {
    switch (task.priority) {
      case 'high': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30';
      case 'low': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800/30';
    }
  };

  const getPriorityIcon = () => {
    switch (task.priority) {
      case 'high': return <AlertCircle className="w-3 h-3" />;
      case 'medium': return <AlertCircle className="w-3 h-3" />;
      case 'low': return <AlertCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`kanban-card mb-3 cursor-pointer ${
            snapshot.isDragging
              ? 'rotate-3 shadow-lg scale-105'
              : 'hover:shadow-md'
          }`}
          style={{
            ...provided.draggableProps.style,
            transform: snapshot.isDragging
              ? `${provided.draggableProps.style?.transform} rotate(3deg) scale(1.05)`
              : provided.draggableProps.style?.transform
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="space-y-3"
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">
                {task.title}
              </h3>
              <div className="flex space-x-1 ml-2">
                <motion.button
                  onClick={onEdit}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  onClick={onDelete}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </motion.button>
              </div>
            </div>

            {/* Content */}
            {task.content && (
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">
                {task.content}
              </p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor()}`}>
                  {getPriorityIcon()}
                  <span className="capitalize">{task.priority}</span>
                </div>
              </div>

              <div className="flex items-center space-x-1 text-xs text-gray-400 dark:text-gray-500">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(task.updatedAt)}</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;
